import {
  Button,
  CountryInput,
  FieldWrapper,
  Icons,
  Input,
  InputErrorMessage,
  Label,
  MultiSelect,
  Textarea,
} from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Country, IncorporationRequestStatus } from '@prisma/client';
import { useEffect, useState } from 'react';
import { useForm } from '../../../../hooks/useZodForm';
import { TUAEIncorporationRequestData } from '../types';
import DMCCBusinessActivitiesDropdownOptions from './../businessActivitiesOptions/dmcc.json';
import SPCBusinessActivitiesDropdownOptions from './../businessActivitiesOptions/spc.json';
import { businessDetailsSchema, TBusinessDetailsFields } from './schema';

type TBusinessDetailsProps = {
  workRequest: WorkRequestResponseType & {
    requestData: TUAEIncorporationRequestData;
  };
  next: () => void;
  mutate: () => void;
  setNotification: TSetNotification;
};

export const BusinessDetails = ({
  workRequest,
  next,
  mutate,
  setNotification,
}: TBusinessDetailsProps) => {
  const [
    businessActivitiesDropdownOptions,
    setBusinessActivitiesDropdownOptions,
  ] = useState<{ label: string; value: string }[]>([]);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm<TBusinessDetailsFields>({
    resolver: zodResolver(businessDetailsSchema),
    defaultValues: {
      businessActivity: workRequest.requestData?.businessActivity,
      businessDescription: workRequest.requestData?.businessDescription,
      estimatedAnnualTurnover: workRequest.requestData?.estimatedAnnualTurnover,
      exportCountries: workRequest.requestData?.exportCountries,
      importCountries: workRequest.requestData?.importCountries,
    },
  });

  const onSubmit = async (formData: TBusinessDetailsFields) => {
    const res = await fetcher(
      `workRequest/${workRequest.id}`,
      {
        method: 'POST',
        body: {
          requestData: {
            ...(workRequest.requestData || {}),
            ...formData,
          },
          status: IncorporationRequestStatus.InProgress,
        },
      },
      undefined
    );

    if (!res.ok) {
      setNotification({
        message:
          'Something went wrong, please try again later or contact Team Commenda if you need help',
        show: true,
        valid: false,
        title: 'Error',
      });
      return;
    }
    await mutate();
    next();
  };

  useEffect(() => {
    if (
      workRequest.requestData?.incorporationJurisdiction ===
      'JUR_AE_DU__FZ_DMCC'
    )
      setBusinessActivitiesDropdownOptions(
        DMCCBusinessActivitiesDropdownOptions
      );
    else if (
      workRequest.requestData?.incorporationJurisdiction === 'JUR_AE_SH__FZ_SPC'
    )
      setBusinessActivitiesDropdownOptions(
        SPCBusinessActivitiesDropdownOptions
      );
  }, []);

  return (
    <div className="flex flex-col gap-10">
      <h2 className="txt-large-bold">Business Activities</h2>

      <FieldWrapper _className="flex flex-col gap-2">
        <Label className="txt-medium-bold">Tell us about your business</Label>
        <p className="txt-small-regular">
          In a few short sentences, please provide an overview of your business.
        </p>
        <div className="mt-2">
          <Textarea
            className="txt-small-regular"
            placeholder="Write a description"
            value={watch('businessDescription')}
            onChange={(e) => {
              setValue('businessDescription', e.target.value);
              clearErrors('businessDescription');
            }}
            error={errors?.businessDescription?.message}
          />
        </div>
      </FieldWrapper>

      <FieldWrapper _className="flex flex-col gap-2">
        <Label className="txt-medium-bold">
          What business activities do you plan to pursue?
        </Label>
        <p className="txt-small-regular">
          Please add upto five business activities.
        </p>
        <div className="flex flex-col gap-2 mt-2">
          <MultiSelect
            options={businessActivitiesDropdownOptions}
            onValueChange={(val) => {
              setValue('businessActivity', val);
              clearErrors('businessActivity');
            }}
            defaultValue={watch('businessActivity')}
            error={errors?.businessActivity?.message}
            hideSelectAll
            maxCount={5}
            placeholder="Select business activities"
          />
          {errors?.businessActivity?.message ? (
            <InputErrorMessage message={errors.businessActivity.message} />
          ) : null}
        </div>
      </FieldWrapper>

      <FieldWrapper _className="flex flex-col gap-2">
        <Label className="txt-medium-bold">
          What is your estimated annual turnover for your new entity?
        </Label>
        <p className="txt-small-regular">First year only in AED</p>
        <div className="mt-2">
          <Input
            className="pl-12"
            type="number"
            leftEl={() => (
              <div className="w-10 p-2.5">
                <span>AED</span>
              </div>
            )}
            value={watch('estimatedAnnualTurnover')}
            onChange={(e) => {
              setValue('estimatedAnnualTurnover', parseInt(e.target.value, 10));
              clearErrors('estimatedAnnualTurnover');
            }}
            error={errors?.estimatedAnnualTurnover?.message}
          />
        </div>
      </FieldWrapper>

      <FieldWrapper _className="flex flex-col gap-2">
        <Label className="txt-medium-bold">
          What countries do you plan to sell goods and services to?
        </Label>
        <p className="txt-small-regular">
          Please submit between 1 and 5 countries where your customers will be
          located.
        </p>
        <div className="mt-2">
          <CountryInput
            type="multiple"
            placeholder="Select country(s)"
            value={watch('exportCountries')}
            onValueChange={(countries) => {
              setValue('exportCountries', countries as Country[]);
              clearErrors('exportCountries');
            }}
            error={errors?.exportCountries?.message}
          />
        </div>
      </FieldWrapper>

      <FieldWrapper _className="flex flex-col gap-2">
        <Label className="txt-medium-bold">
          What countries will supply your goods/servcies?
        </Label>
        <p className="txt-small-regular">
          Please submit between 1 and 5 countries where your suppliers or staff
          will be located.
        </p>
        <div className="mt-2">
          <CountryInput
            type="multiple"
            placeholder="Select country(s)"
            value={watch('importCountries')}
            onValueChange={(countries) => {
              setValue('importCountries', countries as Country[]);
              clearErrors('importCountries');
            }}
            error={errors?.importCountries?.message}
          />
        </div>
      </FieldWrapper>

      <div>
        <Button size="lg" onClick={handleSubmit(onSubmit)}>
          <span>Proceed</span>
          <Icons.ArrowRight className="size-5" />
        </Button>
      </div>
    </div>
  );
};
